import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  varchar,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { users } from "./auth";

// Email account connections (OAuth tokens)
export const emailAccounts = pgTable(
  "email_accounts",
  {
    id: serial("id").primaryKey(),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    provider: varchar("provider", { length: 20 }).notNull(), // "gmail" | "outlook"
    email: varchar("email", { length: 255 }).notNull(),
    accessToken: text("access_token").notNull(),
    refreshToken: text("refresh_token"),
    tokenExpiresAt: timestamp("token_expires_at"),
    syncCursor: text("sync_cursor"), // Gmail historyId / Outlook deltaLink
    lastSyncedAt: timestamp("last_synced_at"),
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    userIdIdx: index("idx_email_accounts_user_id").on(table.userId),
    emailIdx: index("idx_email_accounts_email").on(table.email),
    userProviderUnique: uniqueIndex("uq_email_accounts_user_provider_email").on(
      table.userId,
      table.provider,
      table.email
    ),
  })
);

// Synced emails storage
export const syncedEmails = pgTable(
  "synced_emails",
  {
    id: serial("id").primaryKey(),
    emailAccountId: integer("email_account_id")
      .notNull()
      .references(() => emailAccounts.id, { onDelete: "cascade" }),
    providerMessageId: text("provider_message_id").notNull(),
    threadId: text("thread_id"),
    subject: text("subject"),
    snippet: text("snippet"),
    fromAddress: text("from_address"),
    fromName: text("from_name"),
    toAddresses: text("to_addresses"), // JSON array
    ccAddresses: text("cc_addresses"), // JSON array
    bodyHtml: text("body_html"),
    bodyText: text("body_text"),
    isRead: boolean("is_read").default(false),
    isStarred: boolean("is_starred").default(false),
    labels: text("labels"), // JSON array (Gmail labels / Outlook folders)
    hasAttachments: boolean("has_attachments").default(false),
    receivedAt: timestamp("received_at").notNull(),
    syncedAt: timestamp("synced_at").defaultNow(),
  },
  (table) => ({
    emailAccountIdIdx: index("idx_synced_emails_account_id").on(
      table.emailAccountId
    ),
    providerMsgIdx: index("idx_synced_emails_provider_msg").on(
      table.emailAccountId,
      table.providerMessageId
    ),
    receivedAtIdx: index("idx_synced_emails_received_at").on(table.receivedAt),
    threadIdIdx: index("idx_synced_emails_thread_id").on(table.threadId),
  })
);

// Email attachments metadata
export const emailAttachments = pgTable("email_attachments", {
  id: serial("id").primaryKey(),
  syncedEmailId: integer("synced_email_id")
    .notNull()
    .references(() => syncedEmails.id, { onDelete: "cascade" }),
  providerAttachmentId: text("provider_attachment_id"),
  filename: text("filename").notNull(),
  mimeType: varchar("mime_type", { length: 255 }),
  size: integer("size"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Insert schemas
export const insertEmailAccountSchema = createInsertSchema(emailAccounts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertSyncedEmailSchema = createInsertSchema(syncedEmails).omit({
  id: true,
  syncedAt: true,
});

export const insertEmailAttachmentSchema = createInsertSchema(emailAttachments).omit({
  id: true,
  createdAt: true,
});

// Types
export type EmailAccount = typeof emailAccounts.$inferSelect;
export type InsertEmailAccount = z.infer<typeof insertEmailAccountSchema>;
export type SyncedEmail = typeof syncedEmails.$inferSelect;
export type InsertSyncedEmail = z.infer<typeof insertSyncedEmailSchema>;
export type EmailAttachment = typeof emailAttachments.$inferSelect;
export type InsertEmailAttachment = z.infer<typeof insertEmailAttachmentSchema>;

// Provider type enum for type safety
export type EmailProvider = "gmail" | "outlook";
